import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Radar, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { signalsApi, Signal, SignalSeverity } from '@/lib/signals';
import { AppShell } from '@/components/app/AppShell';
import { UpgradeWall } from '@/components/app/UpgradeWall';
import { isPlanGateError } from '@/lib/planGate';
import { signalKindLabel } from '@/components/app/SignalsPanel';
import { useToast, errMessage } from '@/components/Toast';

function lastMonthRange(): { start: string; end: string } {
  const now = new Date();
  const firstThis = new Date(now.getFullYear(), now.getMonth(), 1);
  const start = new Date(firstThis.getFullYear(), firstThis.getMonth() - 1, 1);
  const end = new Date(firstThis.getFullYear(), firstThis.getMonth(), 0);
  const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { start: iso(start), end: iso(end) };
}

const DOT: Record<SignalSeverity, string> = { high: 'bg-rose-500', medium: 'bg-amber-500', low: 'bg-ink-300' };

function pctText(s: Signal): string {
  if (s.deltaPct === null) return s.kind === 'new_account' ? 'new' : '—';
  const v = Math.round(s.deltaPct * 100);
  return `${v >= 0 ? '+' : ''}${v}%`;
}
const fmt = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: 0 });

export function SignalsPage() {
  const { t } = useTranslation();
  const { wsId } = useParams<{ wsId: string }>();
  const toast = useToast();
  const init = lastMonthRange();
  const [periodStart, setPeriodStart] = useState(init.start);
  const [periodEnd, setPeriodEnd] = useState(init.end);
  const [windowMonths, setWindowMonths] = useState(6);

  const scan = useMutation({
    mutationFn: () => signalsApi.detect(wsId!, { periodStart, periodEnd, windowMonths }),
    onError: (e) => toast.error(errMessage(e, t('signals.error', { defaultValue: 'Could not scan for signals.' }))),
  });

  const planGated = scan.error && isPlanGateError(scan.error);
  const result = scan.data;

  return (
    <AppShell
      title={t('signals.title', { defaultValue: 'Signals' })}
      subtitle={t('signals.subtitle', { defaultValue: 'Automatic anomaly detection over your actuals and budget variances.' })}
    >
      <main className="mx-auto max-w-5xl px-6 py-10 space-y-6">
        <div className="flex items-center gap-2">
          <Radar className="h-6 w-6 text-emerald-700" />
          <div>
            <h1 className="text-2xl font-semibold text-foreground tracking-tight">{t('signals.title', { defaultValue: 'Signals' })}</h1>
            <p className="text-sm text-ink-600">{t('signals.subtitle', { defaultValue: 'Automatic anomaly detection over your actuals and budget variances.' })}</p>
          </div>
        </div>

        {/* Control bar */}
        <div className="rounded-md border border-border bg-card p-4 flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            {t('signals.period_start', { defaultValue: 'Period start' })}
            <input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} className="rounded border border-border bg-white px-2 py-1.5 text-sm font-normal normal-case text-foreground" />
          </label>
          <label className="flex flex-col gap-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            {t('signals.period_end', { defaultValue: 'Period end' })}
            <input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} className="rounded border border-border bg-white px-2 py-1.5 text-sm font-normal normal-case text-foreground" />
          </label>
          <label className="flex flex-col gap-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            {t('signals.window', { defaultValue: 'Compare vs (months)' })}
            <input type="number" min={2} max={36} value={windowMonths} onChange={(e) => setWindowMonths(Math.max(2, Math.min(36, Number(e.target.value) || 6)))} className="w-24 rounded border border-border bg-white px-2 py-1.5 text-sm font-normal normal-case text-foreground" />
          </label>
          <button
            onClick={() => scan.mutate()}
            disabled={scan.isPending}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-60"
          >
            <Radar className="h-4 w-4" /> {scan.isPending ? t('signals.scanning', { defaultValue: 'Scanning…' }) : t('signals.scan', { defaultValue: 'Scan for signals' })}
          </button>
        </div>

        {planGated && wsId && (
          <UpgradeWall wsId={wsId} requiredTier="growth" featureName={t('signals.feature_name', { defaultValue: 'Anomaly detection' })} />
        )}

        {!planGated && result && (
          <div className="space-y-3">
            <p className="text-[12px] text-muted-foreground">
              {t('signals.scanned', {
                defaultValue: 'Scanned {{accounts}} accounts across {{months}} months — {{found}} signal(s) found.',
                accounts: result.scanned.accounts,
                months: result.scanned.months,
                found: result.signals.length,
              })}
            </p>
            {result.signals.length === 0 ? (
              <div className="rounded-md border border-border bg-card px-5 py-8 text-center text-sm text-muted-foreground">
                {t('signals.clean', { defaultValue: 'No anomalies in this period. Everything is within expected ranges.' })}
              </div>
            ) : (
              <div className="rounded-md border border-border bg-card overflow-x-auto">
                <table className="w-full text-[13px] min-w-[640px]">
                  <thead>
                    <tr className="bg-ink-50/70 border-b border-border text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                      <th className="text-left px-4 py-2 font-medium">{t('signals.th_account', { defaultValue: 'Account' })}</th>
                      <th className="text-left px-4 py-2 font-medium">{t('signals.th_kind', { defaultValue: 'Signal' })}</th>
                      <th className="text-right px-4 py-2 font-medium">{t('signals.th_current', { defaultValue: 'Current' })}</th>
                      <th className="text-right px-4 py-2 font-medium">{t('signals.th_baseline', { defaultValue: 'Baseline' })}</th>
                      <th className="text-right px-4 py-2 font-medium">{t('signals.th_change', { defaultValue: 'Change' })}</th>
                      <th className="px-4 py-2" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {result.signals.map((s) => (
                      <tr key={s.id} className="hover:bg-ink-50/40">
                        <td className="px-4 py-2">
                          <span className="inline-flex items-center gap-2">
                            <span className={`h-2 w-2 rounded-full shrink-0 ${DOT[s.severity]}`} aria-label={s.severity} />
                            <span className="num text-ink-800">{s.accountCode}</span>
                          </span>
                        </td>
                        <td className="px-4 py-2 text-muted-foreground">{signalKindLabel(t, s.kind)}</td>
                        <td className="num px-4 py-2 text-right tabular-nums">{fmt(s.current)}</td>
                        <td className="num px-4 py-2 text-right tabular-nums text-muted-foreground">{s.baseline === null ? '—' : fmt(s.baseline)}</td>
                        <td className={`num px-4 py-2 text-right font-semibold ${s.direction === 'up' ? 'text-emerald-700' : 'text-rose-600'}`}>
                          <span className="inline-flex items-center gap-0.5 justify-end">
                            {s.direction === 'up' ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                            {pctText(s)}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-right">
                          <Link to={`/app/workspace/${wsId}/account-search?q=${encodeURIComponent(s.accountCode)}`} className="text-[12px] font-semibold text-emerald-700 hover:text-emerald-800">
                            {t('signals.drill', { defaultValue: 'Drill →' })}
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {!scan.data && !scan.isPending && !planGated && (
          <div className="rounded-md border border-dashed border-border bg-card/50 px-5 py-10 text-center text-sm text-muted-foreground">
            {t('signals.empty_hint', { defaultValue: 'Pick a period and scan to surface budget breaches, trend spikes, and new or stopped accounts.' })}
          </div>
        )}
      </main>
    </AppShell>
  );
}
